// NodeSelectModal.jsx

import React, { useEffect, useMemo, useState } from 'react';
import { isEnumCompatibleFieldType } from 'utilities/FieldTypes';

// Context
import { useCurrentLanguage } from 'context/LanguageContext';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// Utilities
import { getOptionLabel, resolveEnumOptionsForField } from 'utilities/EnumOptions';
import { getResolvedFields } from 'utilities/NodeType';

// CSS
import './NodeSelectModal.css';

// Components
import Button from 'components/button/Button';
import ModalButtons from 'components/modal/buttons/ModalButtons';
import ModalForm from 'components/modal/form/ModalForm';
import Modal from 'components/modal/Modal';
import SelectInput from 'components/selectinput/SelectInput';

/**
 * NodeSelectModal
 *
 * A modal component for selecting a value from an enum field of a node. It retrieves 
 * the options based on the node type and field name, and allows the user to select and save a new value.
 * 
 * @param {object} node - The node object containing the type and properties
 * @param {string} fieldName - The name of the enum field to edit
 * @param {string} label - The label to display in the modal header
 * @param {string} value - The current value of the field
 * @param {boolean} isOpen - Whether the modal is open
 * @param {function} setOpen - Function to set the open state of the modal
 * @param {function} onChange - Function to call with the new value when saved
 * 
 * @returns {ReactNode} The rendered NodeSelectModal component
 */
const NodeSelectModal = ({ node, fieldName, label, value, isOpen, setOpen, onChange }) => {

    // ----- STATE ----- //

    // Local state for the currently selected value in the modal

    const [selected, setSelected] = useState('');

    // ----- DEVICE DETECTION ----- //

    // Mobile detection

    const isMobile = useIsMobile();

    // ----- CONTEXT ----- //

    // Language context

    const language = useCurrentLanguage();

    // ----- MEMOS ----- //

    /**
     * Memoize the options for the select input based on the node type and field name. This ensures 
     * that we only recompute the options when the node type or field name changes.
     */

    const options = useMemo(() => {
        const fields = getResolvedFields(node?.type, { includeHidden: true, excludeSecured: true });
        const fieldDef = fields.find((f) => f.name === fieldName && f.type === 'enum') || null;
        return resolveEnumOptionsForField(fieldDef, language);
    }, [node?.type, fieldName, language]);

    // ----- EFFECTS ----- //

    /**
     * When the modal opens, set the selected value to the current value of the field. This ensures that
     * the user sees the current value pre-selected when they open the modal to edit it.
     */

    useEffect(() => {
        if (isOpen) setSelected(value || '');
    }, [isOpen, value]);

    // ----- HANDLERS ----- //

    /**
     * Handle saving the selected value
     */

    const handleSave = () => {
        onChange(selected);
        setOpen(false);
    };

    // ----- RENDER ----- //

    return (
        <Modal
          title={label || 'Edit'}
          isOpen={isOpen}
          width={isMobile ? '100%' : '400px'}
          height="auto"
          onClose={() => setOpen(false)}
        >
            <ModalForm>

                {/* WRAPPER */}
                <div className="node-select-modal-wrapper">
                    <SelectInput
                      style={{
                        width: '100%',
                      }}
                      value={selected}
                      options={options.map((opt) => ({
                        value: opt.value,
                        label: getOptionLabel(opt, language),
                      }))}
                      placeholder="Select..."
                      onChange={(e) => setSelected(e.target.value)}
                    />
                </div>
            </ModalForm>
            <ModalButtons>
                <Button label="Save" onClick={handleSave} />
            </ModalButtons>
        </Modal>
    );
};

// Dev Tools
NodeSelectModal.displayName = 'NodeSelectModal';

export default NodeSelectModal;
